// app/javascript/components/Chat/DataOutput.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Drawer,
  SwipeableDrawer,
  useMediaQuery,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useChatContext } from "../../contexts/ChatContext";
import { useAppContext } from "../../contexts/AppContext";
import { LoadingPhasesBox } from "./ResponseWaiting";
import { isDarkMode } from "../../utils/constants";
import MarketProLogo from "../../../javascript/images/MarketProLogo.png";
import MarketProLogoLight from "../../../javascript/images/MarketProLogoLight.png";
import { keyframes } from "@emotion/react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import CandlestickChartRoundedIcon from "@mui/icons-material/CandlestickChartRounded";
import StackedLineChartRoundedIcon from "@mui/icons-material/StackedLineChartRounded";
import { getLoadingPhaseMeta } from "./loadingPhaseVisuals";
import AnalysisTracePanel from "./AnalysisTracePanel";
import {
  DATA_PROCESSES_MAX_WIDTH,
  DATA_PROCESSES_MIN_WIDTH,
  getDataProcessesWidth,
} from "../../hooks/useChatLayout";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const pulse = keyframes`
  0%   { transform: scale(0.65); opacity: 0.5; }
  70%  { transform: scale(1.0); opacity: 0; }
  100% { transform: scale(1.0); opacity: 0; }
`;
const helpPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(49, 122, 255, 0.45); }
  70% { box-shadow: 0 0 0 14px rgba(49, 122, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(49, 122, 255, 0); }
`;

const scanSweep = keyframes`
  0% { transform: translateX(-100%); opacity: 0; }
  18% { opacity: 0.4; }
  100% { transform: translateX(130%); opacity: 0; }
`;

const processCardDropIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-14px) scale(0.985);
  }
  65% {
    opacity: 1;
    transform: translateY(1px) scale(1);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const videoFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const videoPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.72; }
  50% { transform: scale(1.08); opacity: 1; }
`;

const videoBeam = keyframes`
  0% { transform: translateX(-120%); opacity: 0; }
  24% { opacity: 1; }
  100% { transform: translateX(120%); opacity: 0; }
`;

const processPayload = (event) => {
  if (!event) return {};
  if (event.text && typeof event.text === "object") return event.text;
  if (typeof event.text === "string") return { message: event.text };
  if (typeof event === "object") return event;
  return { message: event };
};

const normalizeProcessText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const compactProcessText = (value, limit = 118) => {
  const text = normalizeProcessText(value);
  if (!text || text.length <= limit) return text;
  return `${text.slice(0, Math.max(limit - 1, 0)).trim()}...`;
};

const processEvidenceItems = (payload) => {
  const raw = payload?.evidence_items || payload?.evidenceItems;
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      type: normalizeProcessText(item.type),
      stageKey: normalizeProcessText(item.stage_key || item.stageKey),
      label: normalizeProcessText(item.label),
      value: normalizeProcessText(item.value),
      detail: normalizeProcessText(item.detail),
      ticker: normalizeProcessText(item.ticker),
      source: normalizeProcessText(item.source),
    }))
    .filter((item) => item.label || item.value || item.detail);
};

const processStageLabel = (message, stageKey) => {
  const text = normalizeProcessText(message).toLowerCase();

  if (stageKey === "time_scales" || text.startsWith("focusing on time scales")) return "Time scales";
  if (stageKey === "market_indices") return "Market indices";
  if (stageKey === "quotes") return "Pricing data";
  if (stageKey === "stats") return "Market statistics";
  if (stageKey === "short_setup") return "Short setup";
  if (stageKey === "filings") return "SEC filings";
  if (stageKey === "deep_thinking") return "Deep synthesis";
  if (stageKey === "evidence") return "Evidence packet";
  if (stageKey === "direct_data") return "Direct market data";
  if (stageKey === "specialists") return "Specialist analysis";
  if (stageKey === "synthesis") return "Final synthesis";
  if (stageKey === "response") return "Answer assembly";
  if (stageKey === "plan") return "Planning";
  if (text.startsWith("engaging agentic advanced filtering")) return "Advanced filter setup";
  if (stageKey === "criteria") {
    return text.startsWith("parsing agentic criteria") ? "Screen criteria" : "Advanced filter setup";
  }

  const firstLine = String(message || "").split(/\n/)[0].trim();
  return compactProcessText(firstLine.split(":")[0] || firstLine, 64);
};

const isProcessContextEvidence = (item) => (
  item?.type === "process_context" || item?.source === "request context"
);

const processEvidenceItemsForPreview = (items = []) => {
  const richItems = items.filter((item) => !isProcessContextEvidence(item));
  return richItems.length ? richItems : items;
};

const compactEvidenceLine = (item) => {
  if (!item) return "";
  if (item.ticker && item.value) return `${item.ticker}: ${item.value}`;
  return [item.label, item.value || item.detail].filter(Boolean).join(": ");
};

const processPreview = (record, limit = 130) => {
  const evidenceItems = Array.isArray(record?.evidenceItems) ? record.evidenceItems : [];
  const previewItems = processEvidenceItemsForPreview(evidenceItems);
  if (previewItems.length) {
    const previewCount = ["market_indices", "quotes", "stats", "direct_data"].includes(record?.stageKey) ? 5 : 3;
    return compactProcessText(
      previewItems.slice(0, previewCount).map((item) => compactEvidenceLine(item)).filter(Boolean).join(" | "),
      limit,
    );
  }

  const message = String(record?.message || "");
  const afterColon = message.includes(":") ? message.split(":").slice(1).join(":") : message;
  return compactProcessText(afterColon, limit);
};

const isGenericDataProcess = (message) => [
  "generating response",
  "now aggregating all data for analysis",
].includes(normalizeProcessText(message).toLowerCase());

const evidenceGroupsForProcess = (stageKey, evidenceItems) => {
  if (!evidenceItems.length) return [[stageKey, []]];

  const groups = new Map();
  evidenceItems.forEach((item) => {
    const key = item.stageKey || stageKey || "evidence";
    const existing = groups.get(key) || [];
    groups.set(key, [...existing, item]);
  });

  return Array.from(groups.entries());
};

const evidenceItemKey = (item) => [
  item?.type,
  item?.stageKey,
  item?.ticker,
  item?.label,
  item?.value,
  item?.detail,
].filter(Boolean).join("|");

const evidenceItemRank = (item) => {
  if (!item) return 0;
  let rank = 0;
  if (item.type && item.type !== "process_context") rank += 4;
  if (item.source && item.source !== "request context") rank += 2;
  if (item.ticker) rank += 1;
  if (item.value) rank += 1;
  if (item.detail && item.detail !== item.value) rank += 1;
  return rank;
};

const mergeEvidenceItems = (existingItems = [], incomingItems = []) => {
  const byKey = new Map();
  [...existingItems, ...incomingItems].forEach((item, index) => {
    const key = evidenceItemKey(item) || `item-${index}`;
    if (!byKey.has(key)) byKey.set(key, { item, index });
  });

  return Array.from(byKey.values())
    .sort((a, b) => {
      const rankDelta = evidenceItemRank(b.item) - evidenceItemRank(a.item);
      return rankDelta || a.index - b.index;
    })
    .map((entry) => entry.item)
    .slice(0, 8);
};

const buildProcessRecords = (events = [], doneByMsg = new Map()) => {
  const recordsByKey = new Map();

  events.forEach((event, index) => {
    const payload = processPayload(event);
    const message = String(payload?.message || "").trim();
    if (!message) return;

    const evidenceItems = processEvidenceItems(payload);
    if (!evidenceItems.length && isGenericDataProcess(message)) return;

    const payloadStageKey = String(payload?.stage_key || payload?.stageKey || "").trim();
    evidenceGroupsForProcess(payloadStageKey, evidenceItems).forEach(([stageKey, groupedEvidenceItems]) => {
      const key = stageKey || normalizeProcessText(message).toLowerCase() || message;
      const existing = recordsByKey.get(key);
      const completeOnly = payload.complete === true && !existing;
      const done = doneByMsg.get(message) === true || completeOnly;
      const nextEvidenceItems = mergeEvidenceItems(existing?.evidenceItems || [], groupedEvidenceItems);

      recordsByKey.set(key, {
        key,
        message,
        stageKey,
        label: processStageLabel(message, stageKey),
        done: existing?.done || done,
        evidenceItems: nextEvidenceItems,
        firstIndex: existing?.firstIndex ?? index,
        lastIndex: index,
      });
    });
  });

  const records = Array.from(recordsByKey.values()).sort((a, b) => a.firstIndex - b.firstIndex);
  const activeIndex = records.map((record) => record.done).lastIndexOf(false);

  return records.map((record, index) => ({
    ...record,
    isActive: index === activeIndex,
  }));
};

const ProcessQueueCard = ({
  text,
  detail,
  done = false,
  isActive = false,
  phaseMeta,
  enterIndex = 0,
}) => {
  const isSearchPhase = phaseMeta.kind === "search";
  const isCompact = done && !isActive;
  const eyebrow = isActive ? "Current Process" : "Data Process";
  const iconBackground = done
    ? (isDarkMode ? "rgba(42, 155, 96, 0.22)" : "rgba(44, 174, 103, 0.12)")
    : (isSearchPhase
      ? (isDarkMode ? "rgba(34,211,166,0.16)" : "rgba(34,211,166,0.12)")
      : (isDarkMode ? "rgba(36,157,255,0.16)" : "rgba(36,157,255,0.12)"));
  const iconColor = done
    ? (isDarkMode ? "#B9F6CF" : "#237A48")
    : phaseMeta.accent;
  const sweepBackground = isSearchPhase
    ? "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(113,255,223,0.13) 45%, rgba(255,255,255,0) 100%)"
    : "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(133,196,255,0.12) 45%, rgba(255,255,255,0) 100%)";
  const indicatorGlow = done
    ? (isDarkMode ? "rgba(42, 155, 96, 0.18)" : "rgba(44, 174, 103, 0.12)")
    : (isSearchPhase ? "rgba(34,211,166,0.12)" : "rgba(36,157,255,0.12)");

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        mt: isCompact ? 0.65 : 1,
        mb: isCompact ? 0.75 : 1.35,
        py: isCompact ? 0.85 : 1.25,
        px: 1.1,
        borderRadius: 2,
        border: `1px solid ${phaseMeta.panelBorder}`,
        background: phaseMeta.panelBackground,
        boxShadow: isCompact ? "none" : phaseMeta.panelShadow,
        opacity: isCompact ? 0.92 : 1,
        animation: `${processCardDropIn} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        animationDelay: `${Math.min(enterIndex, 6) * 48}ms`,
        willChange: "transform, opacity",
        transition: "border-color 220ms ease, background 220ms ease, box-shadow 220ms ease",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          transition: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: sweepBackground,
          opacity: isCompact ? 0 : done ? 0.35 : 1,
          animation: isActive ? `${scanSweep} ${isSearchPhase ? 2.6 : 3.2}s ease-in-out infinite` : "none",
        }}
      />

      <Box sx={{ position: "relative", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}>
          <Box
            sx={{
              width: isCompact ? 18 : 20,
              height: isCompact ? 18 : 20,
              flexShrink: 0,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconBackground,
              color: iconColor,
            }}
          >
            {done ? (
              <CheckRoundedIcon sx={{ fontSize: 12 }} />
            ) : isSearchPhase ? (
              <PublicRoundedIcon sx={{ fontSize: 12 }} />
            ) : (
              <AutorenewRounded
                sx={{
                  fontSize: 12,
                  animation: isActive ? `${spin} 1.2s linear infinite` : "none",
                }}
              />
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {!isCompact && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, mb: 0.55 }}>
                <Typography
                  sx={{
                    fontSize: "9.5px",
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: phaseMeta.panelMutedColor,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  }}
                >
                  {eyebrow}
                </Typography>
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: iconColor,
                    boxShadow: `0 0 0 3px ${indicatorGlow}`,
                  }}
                />
              </Box>
            )}
            <Typography
              sx={{
                fontSize: isCompact ? "13px" : "14px",
                lineHeight: 1.32,
                fontWeight: 700,
                color: phaseMeta.panelTextColor,
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                display: "-webkit-box",
                WebkitLineClamp: isCompact ? 2 : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {text}
            </Typography>
            {detail && (
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: isCompact ? "12px" : "12.75px",
                  lineHeight: 1.32,
                  fontWeight: 600,
                  color: phaseMeta.panelMutedColor,
                  display: "-webkit-box",
                  WebkitLineClamp: isCompact ? 2 : 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                }}
              >
                {detail}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const inciteHelpSteps = [
  {
    label: "Context",
    title: "Incite starts by understanding the question behind the question.",
    summary:
      "It interprets the query through the user's trading style, time horizon, goals, portfolio context, and any watchlist or tracked ideas it can use.",
    icon: QueryStatsRoundedIcon,
    accent: "#2F7CF6",
    details: [
      "Clarifies whether the user is looking for a trade, investment thesis, risk check, comparison, report, or alert.",
      "Uses portfolio, watchlist, and prior context when available so the answer is not generic.",
      "Sets the requested analysis depth so the response can be fast, standard, or deeper-thinking.",
    ],
    visual: ["Goal", "Style", "Risk", "Time frame"],
  },
  {
    label: "Map",
    title: "Then it maps the market around the request.",
    summary:
      "Incite identifies tickers, assets, sectors, peers, indices, macro drivers, and option or crypto context that may matter to the decision.",
    icon: HubRoundedIcon,
    accent: "#00A783",
    details: [
      "Finds directly named tickers and nearby names the user may not have mentioned.",
      "Checks sector structure, peer movement, and broad market pressure around the question.",
      "Separates noise from relevant assets before deeper analysis begins.",
    ],
    visual: ["Ticker", "Peers", "Sector", "Macro"],
  },
  {
    label: "Gather",
    title: "It aggregates live market evidence in real time.",
    summary:
      "The system pulls together pricing, technicals, news, fundamentals, options context, market breadth, and other relevant data before writing the answer.",
    icon: TrackChangesRoundedIcon,
    accent: "#D97706",
    details: [
      "Collects current data for assets in the question and the surrounding sector.",
      "Includes macro and cross-asset context when those forces can change the setup.",
      "Adjusts data volume based on subscription tier and requested depth.",
    ],
    visual: ["Prices", "News", "Technicals", "Fundamentals"],
  },
  {
    label: "Analyze",
    title: "The answer is synthesized into a decision framework.",
    summary:
      "Incite weighs the evidence, explains what matters, calls out tradeoffs, and turns the setup into a clear next decision.",
    icon: InsightsRoundedIcon,
    accent: "#8B5CF6",
    details: [
      "Compares bullish and bearish evidence instead of only summarizing data.",
      "Adapts the answer for the user's intended action: enter, exit, hold, compare, hedge, or monitor.",
      "Keeps the reasoning connected to the latest data it found.",
    ],
    visual: ["Bull case", "Bear case", "Trigger", "Risk"],
  },
  {
    label: "Act",
    title: "Finally, Incite turns the response into useful output.",
    summary:
      "It charts the relevant assets or trade ideas, proposes follow-up actions, updates context, and can create ongoing workflows when requested.",
    icon: ShowChartRoundedIcon,
    accent: "#E11D48",
    details: [
      "Charts the stocks, assets, or trade ideas that matter to the query.",
      "Can generate reports, track ideas, update watchlists, or monitor a condition over time.",
      "Supports email, notifications, and recurring agent workflows for follow-through.",
    ],
    visual: ["Chart", "Report", "Task", "Watchlist"],
  },
];

const inciteCapabilityCards = [
  {
    title: "Exportable Reports",
    text: "Turn deep analysis into PDF, Word, Excel, or CSV outputs when the task calls for a durable artifact.",
    icon: ArticleRoundedIcon,
    accent: "#2563EB",
  },
  {
    title: "Agent Workflows",
    text: "Ask Incite to email a brief, notify you when something changes, or run recurring market updates.",
    icon: NotificationsActiveRoundedIcon,
    accent: "#DB6D00",
  },
  {
    title: "Portfolio Context",
    text: "Use watchlists, tracked trade ideas, and portfolio constraints as context for better answers.",
    icon: AccountBalanceWalletRoundedIcon,
    accent: "#059669",
  },
  {
    title: "Depth Control",
    text: "Choose faster answers or deeper thinking when a decision deserves more analysis.",
    icon: TuneRoundedIcon,
    accent: "#7C3AED",
  },
];

const InciteProcessDialog = ({
  open,
  isDesktop,
  stepIndex,
  setStepIndex,
  onClose,
}) => {
  const activeStep = inciteHelpSteps[stepIndex] || inciteHelpSteps[0];
  const ActiveStepIcon = activeStep.icon;
  const progressPct = ((stepIndex + 1) / inciteHelpSteps.length) * 100;
  const bodyTextColor = isDarkMode ? "#CBD5E1" : "#475569";
  const borderColor = isDarkMode ? "rgba(100,116,139,0.36)" : "#E2E8F0";

  const handleNext = () => {
    if (stepIndex === inciteHelpSteps.length - 1) {
      onClose();
      return;
    }
    setStepIndex((idx) => Math.min(inciteHelpSteps.length - 1, idx + 1));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={!isDesktop}
      aria-labelledby="incite-process-title"
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "78vw" },
          maxWidth: 1180,
          height: { xs: "100%", md: "78vh" },
          maxHeight: { xs: "100%", md: 820 },
          borderRadius: { xs: 0, md: 3 },
          overflow: "hidden",
          border: `1px solid ${isDarkMode ? "rgba(78,91,114,0.72)" : "rgba(206,213,225,0.9)"}`,
          boxShadow: isDarkMode
            ? "0 30px 90px rgba(0,0,0,0.72)"
            : "0 30px 90px rgba(15,23,42,0.2)",
          background: isDarkMode
            ? "linear-gradient(180deg, #10141C 0%, #0A0D13 100%)"
            : "linear-gradient(180deg, #FFFFFF 0%, #F7F8FB 100%)",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.7, sm: 2.2 },
            pr: { xs: 6, sm: 7 },
            position: "relative",
            flexShrink: 0,
            borderBottom: `1px solid ${isDarkMode ? "rgba(78,91,114,0.58)" : "#E2E8F0"}`,
            background: isDarkMode
              ? "linear-gradient(135deg, rgba(22,29,42,0.98) 0%, rgba(12,15,22,0.98) 100%)"
              : "linear-gradient(135deg, #FFFFFF 0%, #F2F6FB 100%)",
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close help dialog"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: isDarkMode ? "#E5E7EB" : "#334155",
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 0.8 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#FFFFFF",
                background: "linear-gradient(135deg, #2563EB 0%, #0F766E 100%)",
                boxShadow: "0 12px 28px rgba(37,99,235,0.28)",
              }}
            >
              <HelpRoundedIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: 0,
                color: isDarkMode ? "#A7F3D0" : "#0F766E",
              }}
            >
              Incite process guide
            </Typography>
          </Box>
          <Typography
            id="incite-process-title"
            sx={{
              fontSize: { xs: "24px", sm: "30px" },
              lineHeight: 1.08,
              fontWeight: 800,
              letterSpacing: 0,
              color: isDarkMode ? "#F8FAFC" : "#0F172A",
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            Learn how to make money with Incite.
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: "14px",
              lineHeight: 1.55,
              color: bodyTextColor,
              maxWidth: 760,
            }}
          >
            Incite turns a market question into a decision workflow: understand the goal, gather the right evidence,
            analyze it at the requested depth, then chart and track what matters.
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "260px minmax(0, 1fr)" },
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDarkMode ? "#374151" : "#CBD5E1",
              borderRadius: 8,
            },
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRight: { xs: "none", md: `1px solid ${borderColor}` },
              borderBottom: { xs: `1px solid ${borderColor}`, md: "none" },
              backgroundColor: isDarkMode ? "rgba(15,23,42,0.54)" : "rgba(248,250,252,0.88)",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: 0,
                color: isDarkMode ? "#E2E8F0" : "#334155",
                mb: 1.3,
              }}
            >
              Five-step workflow
            </Typography>
            <Box sx={{ display: "grid", gap: 0.75 }}>
              {inciteHelpSteps.map((step, idx) => {
                const StepIcon = step.icon;
                const isSelected = idx === stepIndex;
                return (
                  <Button
                    key={step.label}
                    onClick={() => setStepIndex(idx)}
                    startIcon={<StepIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      borderRadius: 2,
                      px: 1.2,
                      py: 1,
                      minHeight: 44,
                      color: isSelected ? "#FFFFFF" : (isDarkMode ? "#CBD5E1" : "#334155"),
                      backgroundColor: isSelected ? step.accent : "transparent",
                      border: `1px solid ${isSelected ? step.accent : borderColor}`,
                      boxShadow: isSelected ? `0 10px 22px ${step.accent}33` : "none",
                      "&:hover": {
                        backgroundColor: isSelected ? step.accent : (isDarkMode ? "rgba(30,41,59,0.88)" : "#FFFFFF"),
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0, textAlign: "left" }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 800, lineHeight: 1.15 }}>
                        {idx + 1}. {step.label}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.2,
                          fontSize: "11px",
                          lineHeight: 1.25,
                          color: isSelected ? "rgba(255,255,255,0.82)" : (isDarkMode ? "#94A3B8" : "#64748B"),
                        }}
                      >
                        {step.visual.join(" / ")}
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Box>
            <Box sx={{ mt: 2, height: 8, borderRadius: 999, bgcolor: isDarkMode ? "#1F2937" : "#E2E8F0", overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${progressPct}%`,
                  height: "100%",
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${activeStep.accent}, #0F766E)`,
                  transition: "width 220ms ease",
                }}
              />
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, minWidth: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 340px" },
                gap: { xs: 2, md: 2.5 },
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 2,
                  border: `1px solid ${borderColor}`,
                  backgroundColor: isDarkMode ? "rgba(15,23,42,0.72)" : "#FFFFFF",
                  boxShadow: isDarkMode ? "none" : "0 16px 40px rgba(15,23,42,0.07)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.8 }}>
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#FFFFFF",
                      backgroundColor: activeStep.accent,
                      boxShadow: `0 14px 30px ${activeStep.accent}3D`,
                      flexShrink: 0,
                    }}
                  >
                    <ActiveStepIcon sx={{ fontSize: 25 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 800, color: activeStep.accent }}>
                      Step {stepIndex + 1} of {inciteHelpSteps.length}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.2,
                        fontSize: { xs: "20px", sm: "24px" },
                        lineHeight: 1.16,
                        fontWeight: 800,
                        letterSpacing: 0,
                        color: isDarkMode ? "#F8FAFC" : "#111827",
                      }}
                    >
                      {activeStep.title}
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: "14px", lineHeight: 1.65, color: bodyTextColor, maxWidth: 720 }}>
                  {activeStep.summary}
                </Typography>

                <Box sx={{ mt: 2.4, display: "grid", gap: 1 }}>
                  {activeStep.details.map((detail) => (
                    <Box
                      key={detail}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${borderColor}`,
                        backgroundColor: isDarkMode ? "rgba(2,6,23,0.34)" : "#F8FAFC",
                      }}
                    >
                      <CheckRoundedIcon sx={{ mt: 0.15, fontSize: 17, color: activeStep.accent, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "13px", lineHeight: 1.5, color: isDarkMode ? "#E2E8F0" : "#334155" }}>
                        {detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${borderColor}`,
                  background: isDarkMode
                    ? "linear-gradient(180deg, rgba(15,23,42,0.82), rgba(2,6,23,0.74))"
                    : "linear-gradient(180deg, #FFFFFF, #F8FAFC)",
                  minHeight: 320,
                }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 800, color: isDarkMode ? "#F8FAFC" : "#0F172A" }}>
                  What Incite is building
                </Typography>
                <Box
                  sx={{
                    mt: 1.4,
                    height: 142,
                    borderRadius: 2,
                    position: "relative",
                    overflow: "hidden",
                    border: `1px solid ${borderColor}`,
                    backgroundColor: isDarkMode ? "rgba(2,6,23,0.68)" : "#F1F5F9",
                  }}
                >
                  {[38, 58, 48, 74, 62, 88, 76].map((height, idx) => (
                    <Box
                      key={`${activeStep.label}-${idx}`}
                      sx={{
                        position: "absolute",
                        left: `${8 + idx * 13}%`,
                        bottom: 20,
                        width: "7%",
                        height: `${height}%`,
                        maxWidth: 28,
                        borderRadius: "8px 8px 2px 2px",
                        backgroundColor: idx === stepIndex ? activeStep.accent : (isDarkMode ? "#334155" : "#CBD5E1"),
                        opacity: idx === stepIndex ? 1 : 0.72,
                        transition: "height 220ms ease, background-color 220ms ease",
                      }}
                    />
                  ))}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 16,
                      right: 16,
                      bottom: 18,
                      height: 1,
                      bgcolor: isDarkMode ? "rgba(148,163,184,0.28)" : "rgba(100,116,139,0.3)",
                    }}
                  />
                </Box>
                <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.9 }}>
                  {activeStep.visual.map((item) => (
                    <Box
                      key={item}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        border: `1px solid ${borderColor}`,
                        backgroundColor: isDarkMode ? "rgba(15,23,42,0.62)" : "#FFFFFF",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: isDarkMode ? "#E2E8F0" : "#334155" }}>
                        {item}
                      </Typography>
                      <Box sx={{ mt: 0.8, height: 5, borderRadius: 999, bgcolor: `${activeStep.accent}33`, overflow: "hidden" }}>
                        <Box sx={{ width: `${54 + item.length * 4}%`, maxWidth: "100%", height: "100%", bgcolor: activeStep.accent }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ mt: 1.5, fontSize: "12px", lineHeight: 1.5, color: isDarkMode ? "#94A3B8" : "#64748B" }}>
                  The goal is a repeatable process, not a promise of returns. Incite gives decision support; the user
                  still controls position size, risk, and final action.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2.5,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", xl: "repeat(4, 1fr)" },
                gap: 1.2,
              }}
            >
              {inciteCapabilityCards.map((card) => {
                const CapabilityIcon = card.icon;
                return (
                  <Box
                    key={card.title}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${borderColor}`,
                      backgroundColor: isDarkMode ? "rgba(15,23,42,0.58)" : "#FFFFFF",
                      minHeight: 124,
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: card.accent,
                        backgroundColor: `${card.accent}18`,
                        mb: 1,
                      }}
                    >
                      <CapabilityIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 800, color: isDarkMode ? "#F8FAFC" : "#111827" }}>
                      {card.title}
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: "12px", lineHeight: 1.45, color: bodyTextColor }}>
                      {card.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 1.6,
            borderTop: `1px solid ${isDarkMode ? "rgba(78,91,114,0.58)" : "#E2E8F0"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            backgroundColor: isDarkMode ? "rgba(10,13,19,0.96)" : "#FFFFFF",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: isDarkMode ? "#94A3B8" : "#64748B" }}>
            {activeStep.label}: {activeStep.visual.join(" / ")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              disabled={stepIndex === 0}
              onClick={() => setStepIndex((idx) => Math.max(0, idx - 1))}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 1.6,
                color: isDarkMode ? "#E2E8F0" : "#334155",
                borderColor: isDarkMode ? "rgba(148,163,184,0.42)" : "#CBD5E1",
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 2,
                px: 1.8,
                backgroundColor: activeStep.accent,
                boxShadow: `0 10px 22px ${activeStep.accent}33`,
                "&:hover": {
                  backgroundColor: activeStep.accent,
                  filter: "brightness(0.94)",
                },
              }}
            >
              {stepIndex === inciteHelpSteps.length - 1 ? "Finish" : "Next step"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const inciteVideoScenes = [
  { title: "Query", caption: "What should I do with NVDA this week?" },
  { title: "Relevant Tickers", caption: "Incite expands into the semiconductor universe." },
  { title: "Market Context", caption: "Economy, news, sector trend, price movement." },
  { title: "Live Evidence", caption: "The data is gathered and weighted." },
  { title: "Analysis", caption: "Incite turns signals into a decision frame." },
  { title: "Action", caption: "Charts, reports, alerts, and watchlist updates." },
];

const videoPlaybackSpeeds = [1, 1.5, 2, 0.5];

const InciteProcessVideoDialog = ({ open, isDesktop, onClose }) => {
  const [progress, setProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  useEffect(() => {
    if (!open) return;
    setProgress(0);
    setPlaybackSpeed(1);
  }, [open]);

  useEffect(() => {
    if (!open || progress >= 100) return;
    const tickMs = 80;
    const durationMs = 22000;
    const timer = setInterval(() => {
      setProgress((current) => Math.min(100, current + ((tickMs / durationMs) * 100 * playbackSpeed)));
    }, tickMs);
    return () => clearInterval(timer);
  }, [open, playbackSpeed, progress]);

  const sceneIndex = Math.min(
    inciteVideoScenes.length - 1,
    Math.floor((progress / 100) * inciteVideoScenes.length),
  );
  const scene = inciteVideoScenes[sceneIndex];
  const isEnded = progress >= 100;
  const animationDuration = (seconds) => `${seconds / playbackSpeed}s`;
  const reveal = (start, end = start + 12) => {
    if (progress <= start) return 0;
    if (progress >= end) return 1;
    return (progress - start) / (end - start);
  };
  const cycleSpeed = () => {
    const idx = videoPlaybackSpeeds.indexOf(playbackSpeed);
    setPlaybackSpeed(videoPlaybackSpeeds[(idx + 1) % videoPlaybackSpeeds.length]);
  };
  const handleControlClick = () => {
    if (isEnded) {
      setProgress(0);
      setPlaybackSpeed(1);
      return;
    }
    cycleSpeed();
  };

  const textColor = isDarkMode ? "#F8FAFC" : "#0F172A";
  const mutedColor = isDarkMode ? "#A7B3C5" : "#536174";
  const panelBg = isDarkMode ? "rgba(9, 13, 20, 0.92)" : "rgba(255,255,255,0.95)";
  const borderColor = isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(203,213,225,0.8)";
  const nodeBg = isDarkMode ? "rgba(15,23,42,0.84)" : "rgba(255,255,255,0.92)";
  const queryReveal = reveal(0, 8);
  const tickerReveal = reveal(12, 28);
  const contextReveal = reveal(32, 48);
  const evidenceReveal = reveal(48, 64);
  const tickerNodes = [
    { label: "AMD", x: "30%", y: "24%" },
    { label: "INTC", x: "68%", y: "23%" },
    { label: "AVGO", x: "76%", y: "46%" },
    { label: "TSM", x: "63%", y: "69%" },
    { label: "SMH", x: "34%", y: "68%" },
  ];
  const contextNodes = [
    { label: "Economy", x: "14%", y: "32%" },
    { label: "News", x: "13%", y: "61%" },
    { label: "Tech Sector", x: "31%", y: "30%" },
    { label: "Price Moves", x: "32%", y: "63%" },
  ];
  const branchLines = [
    { left: "42%", top: "41%", width: "17%", rotate: "-28deg", delay: 0 },
    { left: "50%", top: "40%", width: "18%", rotate: "-12deg", delay: 1 },
    { left: "58%", top: "47%", width: "17%", rotate: "4deg", delay: 2 },
    { left: "55%", top: "58%", width: "16%", rotate: "36deg", delay: 3 },
    { left: "36%", top: "58%", width: "17%", rotate: "-34deg", delay: 4 },
  ];
  const contextLines = [
    { left: "21%", top: "43%", width: "12%", rotate: "-50deg", delay: 0 },
    { left: "20%", top: "53%", width: "13%", rotate: "48deg", delay: 1 },
    { left: "23%", top: "42%", width: "12%", rotate: "-26deg", delay: 2 },
    { left: "24%", top: "55%", width: "12%", rotate: "24deg", delay: 3 },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={!isDesktop}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "78vw" },
          maxWidth: 1180,
          height: { xs: "100%", md: "78vh" },
          maxHeight: { xs: "100%", md: 820 },
          borderRadius: { xs: 0, md: 3 },
          overflow: "hidden",
          border: `1px solid ${borderColor}`,
          background: isDarkMode ? "#070A10" : "#F6F8FB",
          boxShadow: isDarkMode
            ? "0 30px 90px rgba(0,0,0,0.72)"
            : "0 30px 90px rgba(15,23,42,0.18)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, height: "100%", position: "relative", overflow: "hidden" }}>
        <IconButton
          onClick={onClose}
          aria-label="Close Incite video"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 12,
            color: isDarkMode ? "#E5E7EB" : "#334155",
            backgroundColor: isDarkMode ? "rgba(15,23,42,0.78)" : "rgba(255,255,255,0.82)",
            "&:hover": {
              backgroundColor: isDarkMode ? "rgba(30,41,59,0.92)" : "#FFFFFF",
            },
          }}
        >
          <CloseRoundedIcon />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: isDarkMode
              ? "linear-gradient(135deg, #071018 0%, #0A0D13 45%, #131019 100%)"
              : "linear-gradient(135deg, #F8FAFC 0%, #EEF6F4 46%, #F5F1FA 100%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: { xs: 22, sm: 28 },
            left: { xs: 18, sm: 28 },
            right: 72,
            zIndex: 4,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "22px", sm: "30px" },
              lineHeight: 1.05,
              fontWeight: 850,
              letterSpacing: 0,
              color: textColor,
            }}
          >
            Incite Intelligence
          </Typography>
          <Typography sx={{ mt: 0.8, fontSize: "13px", color: mutedColor, maxWidth: 420 }}>
            Question to market map to decision support.
          </Typography>
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: { xs: 96, sm: 112 },
            left: { xs: 14, sm: 24 },
            right: { xs: 14, sm: 24 },
            bottom: { xs: 86, sm: 82 },
            zIndex: 2,
            borderRadius: 3,
            border: `1px solid ${borderColor}`,
            backgroundColor: isDarkMode ? "rgba(2,6,23,0.42)" : "rgba(255,255,255,0.54)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.45,
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(148,163,184,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.12) 1px, transparent 1px)"
                : "linear-gradient(rgba(100,116,139,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(100,116,139,0.16) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: 252, sm: 330 },
              minHeight: { xs: 96, sm: 112 },
              borderRadius: "50%",
              p: { xs: 1.5, sm: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              color: textColor,
              backgroundColor: panelBg,
              border: `1px solid ${borderColor}`,
              boxShadow: isDarkMode
                ? "0 0 0 18px rgba(37,99,235,0.08), 0 24px 60px rgba(0,0,0,0.38)"
                : "0 0 0 18px rgba(37,99,235,0.08), 0 24px 60px rgba(15,23,42,0.16)",
              opacity: queryReveal,
              animation: `${videoFloat} ${animationDuration(4.5)} ease-in-out infinite`,
              zIndex: 3,
            }}
          >
            <Typography sx={{ fontSize: "11px", color: mutedColor, fontWeight: 850 }}>
              User Query
            </Typography>
            <Typography sx={{ mt: 0.55, fontSize: { xs: "15px", sm: "18px" }, lineHeight: 1.25, fontWeight: 850 }}>
              What should I do with NVDA this week?
            </Typography>
          </Box>

          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: { xs: 260, sm: 390 },
              height: { xs: 260, sm: 390 },
              transform: "translate(-50%, -50%)",
              borderRadius: "50%",
              border: "1px solid rgba(37,99,235,0.28)",
              opacity: tickerReveal * 0.9,
            }}
          />

          {branchLines.map((line) => (
            <Box
              key={`${line.left}-${line.top}`}
              sx={{
                position: "absolute",
                left: line.left,
                top: line.top,
                width: line.width,
                height: 2,
                borderRadius: 999,
                transform: `rotate(${line.rotate}) scaleX(${reveal(12 + line.delay * 1.2, 24 + line.delay * 1.2)})`,
                transformOrigin: "left center",
                background: "linear-gradient(90deg, rgba(37,99,235,0), #2563EB, rgba(15,118,110,0.2))",
                opacity: tickerReveal,
                zIndex: 1,
              }}
            />
          ))}

          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "26%",
              transform: `translate(-50%, -50%) scale(${0.82 + tickerReveal * 0.18})`,
              opacity: tickerReveal,
              px: 1.35,
              py: 0.85,
              borderRadius: 2,
              border: `1px solid ${borderColor}`,
              backgroundColor: "#0F766E",
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: 900,
              boxShadow: "0 12px 30px rgba(15,118,110,0.28)",
              zIndex: 2,
            }}
          >
            Semiconductors
          </Box>

          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "16%",
              transform: `translate(-50%, -50%) scale(${0.82 + tickerReveal * 0.18})`,
              opacity: tickerReveal,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.7,
              border: `1px solid ${borderColor}`,
              backgroundColor: "#2563EB",
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: 900,
              boxShadow: "0 12px 30px rgba(37,99,235,0.3)",
              zIndex: 2,
            }}
          >
            NVDA
          </Box>

          {tickerNodes.map((node, idx) => (
            <Box
              key={node.label}
              sx={{
                position: "absolute",
                left: node.x,
                top: node.y,
                opacity: reveal(20 + idx * 1.4, 32 + idx * 1.4),
                transform: `translate(-50%, -50%) scale(${0.86 + reveal(20 + idx * 1.4, 32 + idx * 1.4) * 0.14})`,
                px: 1.2,
                py: 0.75,
                borderRadius: 1.7,
                border: `1px solid ${borderColor}`,
                backgroundColor: nodeBg,
                color: textColor,
                fontSize: "12px",
                fontWeight: 850,
                boxShadow: isDarkMode ? "0 10px 28px rgba(0,0,0,0.22)" : "0 10px 28px rgba(15,23,42,0.1)",
              }}
            >
              {node.label}
            </Box>
          ))}

          {contextNodes.map((node, idx) => (
            <Box
              key={node.label}
              sx={{
                position: "absolute",
                left: node.x,
                top: node.y,
                opacity: reveal(34 + idx * 1.8, 48 + idx * 1.8),
                transform: `translate(-50%, -50%) scale(${0.86 + reveal(34 + idx * 1.8, 48 + idx * 1.8) * 0.14})`,
                px: 1.15,
                py: 0.75,
                borderRadius: 1.7,
                border: `1px solid ${borderColor}`,
                backgroundColor: nodeBg,
                color: textColor,
                fontSize: "12px",
                fontWeight: 850,
                boxShadow: isDarkMode ? "0 10px 28px rgba(0,0,0,0.22)" : "0 10px 28px rgba(15,23,42,0.1)",
                zIndex: 2,
              }}
            >
              {node.label}
            </Box>
          ))}

          {contextLines.map((line) => (
            <Box
              key={`${line.left}-${line.rotate}`}
              sx={{
                position: "absolute",
                left: line.left,
                top: line.top,
                width: line.width,
                height: 2,
                borderRadius: 999,
                transform: `rotate(${line.rotate}) scaleX(${reveal(34 + line.delay * 1.2, 46 + line.delay * 1.2)})`,
                transformOrigin: "left center",
                background: "linear-gradient(90deg, rgba(217,119,6,0), #D97706, rgba(217,119,6,0.15))",
                opacity: contextReveal,
                zIndex: 1,
              }}
            />
          ))}

          <Box
            sx={{
              position: "absolute",
              left: "22%",
              top: "49%",
              transform: `translate(-50%, -50%) scale(${0.82 + contextReveal * 0.18})`,
              opacity: contextReveal,
              px: 1.25,
              py: 0.8,
              borderRadius: 2,
              border: `1px solid ${borderColor}`,
              backgroundColor: "#D97706",
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: 900,
              boxShadow: "0 12px 30px rgba(217,119,6,0.24)",
              zIndex: 2,
            }}
          >
            Context Signals
          </Box>

          <Box
            sx={{
              position: "absolute",
              left: { xs: "8%", md: "9%" },
              top: { xs: "13%", md: "15%" },
              width: { xs: 164, sm: 190 },
              opacity: contextReveal,
              transform: `translateY(${(1 - contextReveal) * 18}px)`,
              transition: "opacity 240ms ease, transform 240ms ease",
            }}
          >
            {["Style", "Goals", "Risk"].map((label, idx) => (
              <Box
                key={label}
                sx={{
                  display: "inline-flex",
                  mr: 0.7,
                  mb: 0.7,
                  px: 1,
                  py: 0.55,
                  borderRadius: "999px",
                  border: `1px solid ${borderColor}`,
                  backgroundColor: nodeBg,
                  color: textColor,
                  fontSize: "11px",
                  fontWeight: 800,
                  animation: `${videoPulse} ${animationDuration(2.8 + idx * 0.25)} ease-in-out infinite`,
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          {[0, 1, 2, 3].map((idx) => (
            <Box
              key={`beam-${idx}`}
              sx={{
                position: "absolute",
                right: { xs: "10%", md: "12%" },
                top: `${22 + idx * 10}%`,
                width: { xs: 140, md: 220 },
                height: 5,
                borderRadius: 999,
                overflow: "hidden",
                backgroundColor: isDarkMode ? "rgba(148,163,184,0.12)" : "rgba(100,116,139,0.12)",
                opacity: evidenceReveal,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(90deg, transparent, #22C55E, transparent)",
                  animation: `${videoBeam} ${animationDuration(1.8 + idx * 0.25)} linear infinite`,
                },
              }}
            />
          ))}

          <Box
            sx={{
              position: "absolute",
              right: { xs: "7%", md: "9%" },
              bottom: { xs: "14%", md: "16%" },
              width: { xs: 190, sm: 260 },
              height: { xs: 122, sm: 148 },
              p: 1.4,
              borderRadius: 2,
              border: `1px solid ${borderColor}`,
              backgroundColor: panelBg,
              opacity: reveal(62, 76),
              transform: `translateY(${(1 - reveal(62, 76)) * 22}px)`,
              transition: "opacity 240ms ease, transform 240ms ease",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <CandlestickChartRoundedIcon sx={{ fontSize: 19, color: "#22C55E" }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 850, color: textColor }}>
                Trade setup
              </Typography>
            </Box>
            <Box sx={{ mt: 1.3, height: 68, display: "flex", alignItems: "flex-end", gap: 0.8 }}>
              {[28, 44, 38, 58, 52, 76, 68].map((height, idx) => (
                <Box
                  key={`bar-${idx}`}
                  sx={{
                    flex: 1,
                    height: `${height}%`,
                    borderRadius: "6px 6px 2px 2px",
                    backgroundColor: idx > 4 ? "#22C55E" : (isDarkMode ? "#334155" : "#CBD5E1"),
                    transition: "height 240ms ease",
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              left: { xs: "7%", md: "8%" },
              bottom: { xs: "5%", md: "7%" },
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              width: { xs: "74%", md: "58%" },
              opacity: reveal(78, 90),
              transform: `translateY(${(1 - reveal(78, 90)) * 20}px)`,
              transition: "opacity 240ms ease, transform 240ms ease",
            }}
          >
            {[
              { label: "Chart", icon: StackedLineChartRoundedIcon },
              { label: "Report", icon: ArticleRoundedIcon },
              { label: "Notify", icon: NotificationsActiveRoundedIcon },
              { label: "Watchlist", icon: AccountBalanceWalletRoundedIcon },
            ].map((item) => {
              const OutputIcon = item.icon;
              return (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.7,
                    px: 1.1,
                    py: 0.8,
                    borderRadius: 1.6,
                    border: `1px solid ${borderColor}`,
                    backgroundColor: nodeBg,
                    color: textColor,
                    fontSize: "12px",
                    fontWeight: 850,
                  }}
                >
                  <OutputIcon sx={{ fontSize: 16, color: "#2563EB" }} />
                  {item.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: { xs: 18, sm: 28 },
            bottom: { xs: 20, sm: 22 },
            right: { xs: 124, sm: 150 },
            zIndex: 5,
          }}
        >
          <Typography sx={{ fontSize: "12px", color: mutedColor, fontWeight: 850 }}>
            {scene.title}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: { xs: "14px", sm: "16px" }, color: textColor, fontWeight: 800 }}>
            {scene.caption}
          </Typography>
          <Box sx={{ mt: 1.15, height: 5, borderRadius: 999, bgcolor: isDarkMode ? "#1F2937" : "#E2E8F0", overflow: "hidden" }}>
            <Box
              sx={{
                width: `${progress}%`,
                height: "100%",
                borderRadius: 999,
                background: "linear-gradient(90deg, #2563EB, #0F766E, #22C55E)",
                transition: "width 80ms linear",
              }}
            />
          </Box>
        </Box>

        <Button
          onClick={handleControlClick}
          variant="contained"
          startIcon={isEnded ? <ReplayRoundedIcon /> : <SpeedRoundedIcon />}
          sx={{
            position: "absolute",
            right: { xs: 18, sm: 28 },
            bottom: { xs: 18, sm: 20 },
            zIndex: 7,
            minWidth: 94,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 850,
            backgroundColor: isEnded ? "#0F766E" : "#2563EB",
            boxShadow: isEnded ? "0 12px 28px rgba(15,118,110,0.28)" : "0 12px 28px rgba(37,99,235,0.28)",
            "&:hover": {
              backgroundColor: isEnded ? "#0F766E" : "#2563EB",
              filter: "brightness(0.94)",
            },
          }}
        >
          {isEnded ? "Replay" : `${playbackSpeed}x`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};


const DataOutput = ({ loadingResponse }) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpExperience] = useState("video");
  const [helpStepIndex, setHelpStepIndex] = useState(0);
  const [helpTeaserDismissed, setHelpTeaserDismissed] = useState(false);
  const [showHelpTeaser, setShowHelpTeaser] = useState(false);
  const {
    loadingPhaseMessage,
    dataProcessesQueue,
    analysisTraceEvents,
    isDataProcessesOpen,
    setIsDataProcessesOpen,
    dataProcessesPanelWidth,
    setDataProcessesPanelWidth,
    activeThread,
    liveTextOutputting,
  } =
    useChatContext();
  const { appProps, messagesByThreadUuid, messageThreads } = useAppContext();

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const showHelpTeaserText = useMediaQuery(theme.breakpoints.up("sm"));
  const showInciteHelp = false;
  const canUseAdminLoadingPhases = appProps?.userData?.is_admin_user === true;
  const hasAnalysisTrace =
    canUseAdminLoadingPhases &&
    Array.isArray(analysisTraceEvents) &&
    analysisTraceEvents.length > 0;
  const panelWidth = getDataProcessesWidth(
    hasAnalysisTrace,
    dataProcessesPanelWidth,
    typeof window === "undefined" ? null : window.innerWidth,
  );
  const helpRightOffset = (isDesktop && isDataProcessesOpen ? panelWidth : 0) + 56;

  const [doneByMsg, setDoneByMsg] = useState(() => new Map());
  const [isResizingPanel, setIsResizingPanel] = useState(false);
  const seenIdsRef = useRef(new Set());
  const prevThreadUuidRef = useRef(null);
  const autoOpenedTraceIdsRef = useRef(new Set());
  const panelResizeCleanupRef = useRef(null);

  const activeThreadUuid = activeThread?.threadUuid ?? null;
  const dataProcessesQueueLength = dataProcessesQueue?.length || 0;
  const showProcessingLoader = loadingResponse && !liveTextOutputting;
  const currentTraceId = hasAnalysisTrace
    ? (analysisTraceEvents.find((event) => event?.trace_id)?.trace_id || activeThreadUuid || "active-trace")
    : null;
  const threadMessages = activeThreadUuid
    ? (messagesByThreadUuid?.[activeThreadUuid] || [])
    : (messageThreads || []);
  const hasAskedFirstQuestion = threadMessages.some((message) => {
    const sender = String(message?.sender || "").toLowerCase();
    const text = String(message?.message || "").trim();
    return (sender === "human" || sender === "user") && text.length > 0 && !message?.isButton;
  });

  const shouldPulseHelp = hasAskedFirstQuestion && !helpTeaserDismissed;

  const handleResizePointerDown = (event) => {
    if (!isDesktop || typeof window === "undefined") return;
    event.preventDefault();
    panelResizeCleanupRef.current?.();

    const body = document.body;
    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;

    setIsResizingPanel(true);
    body.style.cursor = "col-resize";
    body.style.userSelect = "none";

    const updateWidth = (pointerEvent) => {
      const nextWidth = getDataProcessesWidth(
        hasAnalysisTrace,
        window.innerWidth - pointerEvent.clientX,
        window.innerWidth,
      );
      setDataProcessesPanelWidth(nextWidth);
    };

    const stopResize = () => {
      setIsResizingPanel(false);
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", updateWidth);
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
      panelResizeCleanupRef.current = null;
    };

    panelResizeCleanupRef.current = stopResize;
    updateWidth(event);
    window.addEventListener("pointermove", updateWidth);
    window.addEventListener("pointerup", stopResize);
    window.addEventListener("pointercancel", stopResize);
  };

  const handleResizeKeyDown = (event) => {
    if (!isDesktop || typeof window === "undefined") return;

    const step = event.shiftKey ? 64 : 24;
    let nextWidth = null;
    if (event.key === "ArrowLeft") nextWidth = panelWidth + step;
    if (event.key === "ArrowRight") nextWidth = panelWidth - step;
    if (event.key === "Home") nextWidth = DATA_PROCESSES_MIN_WIDTH;
    if (event.key === "End") nextWidth = DATA_PROCESSES_MAX_WIDTH;
    if (nextWidth == null) return;

    event.preventDefault();
    setDataProcessesPanelWidth(
      getDataProcessesWidth(hasAnalysisTrace, nextWidth, window.innerWidth),
    );
  };

  useEffect(() => {
    if (!shouldPulseHelp) {
      setShowHelpTeaser(false);
      return;
    }
    const timer = setTimeout(() => setShowHelpTeaser(true), 280);
    return () => clearTimeout(timer);
  }, [shouldPulseHelp, activeThreadUuid]);

  useEffect(() => {
    return () => {
      panelResizeCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    if (!showProcessingLoader) return;
    if (!hasAnalysisTrace) return;
    if (!currentTraceId) return;
    if (autoOpenedTraceIdsRef.current.has(currentTraceId)) return;

    autoOpenedTraceIdsRef.current.add(currentTraceId);
    if (isDataProcessesOpen !== true) {
      setIsDataProcessesOpen(true);
    }
  }, [currentTraceId, hasAnalysisTrace, isDataProcessesOpen, isDesktop, setIsDataProcessesOpen, showProcessingLoader]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const nextWidth = getDataProcessesWidth(
      hasAnalysisTrace,
      dataProcessesPanelWidth,
      window.innerWidth,
    );
    if (dataProcessesPanelWidth && nextWidth !== dataProcessesPanelWidth) {
      setDataProcessesPanelWidth(nextWidth);
    }
  }, [dataProcessesPanelWidth, hasAnalysisTrace, setDataProcessesPanelWidth]);

  useEffect(() => {
    if (!dataProcessesQueue?.length) return;
    for (const evt of dataProcessesQueue) {
      if (!evt?.id) continue;
      if (!seenIdsRef.current.has(evt.id)) {
        seenIdsRef.current.add(evt.id);
        if (evt.text.complete || evt.text.require_completion == false) {
          let timeout = 800;
          if (evt.text.require_completion == false) timeout = 1300;
          setTimeout(() => {
            setDoneByMsg((prevMap) => {
              const m = new Map(prevMap);
              m.set(evt.text.message, true);
              return m;
            });
          }, timeout);
        }
      }
    }
  }, [dataProcessesQueue]); // depend on the array reference, not just length

  useEffect(() => {
    if (loadingResponse && dataProcessesQueueLength === 0) {
      seenIdsRef.current = new Set();
      setDoneByMsg(new Map());
    }
  }, [dataProcessesQueueLength, loadingResponse]);

  useEffect(() => {
    const curr = activeThread?.threadUuid ?? null;
    const prev = prevThreadUuidRef.current;
    // if thread changes, reset it (If it doesn't change from a temp thread).
    if (prev === curr) return; // only run if thread uuid has changed..
    const prevWasTemp = !!prev && prev.startsWith("temp-");
    if (!prevWasTemp) {
      if (activeThread) {
        seenIdsRef.current = new Set();
        setDoneByMsg(new Map());
      }
    }
    prevThreadUuidRef.current = curr;
  }, [activeThread])

  const phaseMeta = getLoadingPhaseMeta(loadingPhaseMessage || "", isDarkMode);
  const processRecords = useMemo(
    () => buildProcessRecords(dataProcessesQueue || [], doneByMsg),
    [dataProcessesQueue, doneByMsg],
  );

  const legacyProcessContent = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        py: 2,
        px: 1,
        pt: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        "& *": { whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere" },
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDarkMode ? "#3a3a3a" : "#cfcfcf",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: isDarkMode ? "#1e1e1e" : "#f3f3f3",
        },
      }}
    >
      {processRecords.length === 0 && (
        <Box sx={{ pl: 1 }}>
          <LoadingPhasesBox
            text={loadingPhaseMessage ? "Awaiting next process." : "Awaiting analysis."}
            sx={{ marginTop: "8px" }}
            hideDotDotDot={!showProcessingLoader}
            preventAnimation={!showProcessingLoader}
          />
        </Box>
      )}

      {processRecords.map((record, idx) => {
        const isActive = showProcessingLoader && record.isActive;
        return (
          <ProcessQueueCard
            key={record.key || `${idx}-${record.message}`}
            text={record.label}
            detail={processPreview(record, isActive ? 360 : 220)}
            done={record.done}
            isActive={isActive}
            phaseMeta={phaseMeta}
            enterIndex={idx}
          />
        );
      })}
    </Box>
  );

  const processContent = hasAnalysisTrace ? (
    <AnalysisTracePanel
      events={analysisTraceEvents}
      legacyContent={processRecords.length > 0 ? legacyProcessContent : null}
      loading={showProcessingLoader}
    />
  ) : legacyProcessContent;

  return (
    <>
      {showInciteHelp && (
        <Box
          sx={{
            position: "fixed",
            top: 8,
            right: helpRightOffset,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            pointerEvents: "none",
            transition: "right 240ms ease",
          }}
        >
          {showHelpTeaserText && (
            <Box
              sx={{
                pointerEvents: "none",
                whiteSpace: "nowrap",
                px: 1.5,
                py: 0.75,
                borderRadius: "999px",
                border: `1px solid ${isDarkMode ? "rgba(72, 138, 255, 0.45)" : "rgba(26, 101, 230, 0.28)"}`,
                background: isDarkMode
                  ? "linear-gradient(110deg, rgba(18,25,40,0.92), rgba(14,18,28,0.9))"
                  : "linear-gradient(115deg, rgba(247,252,255,0.97), rgba(230,240,255,0.95))",
                boxShadow: isDarkMode
                  ? "0 8px 22px rgba(0,0,0,0.32)"
                  : "0 8px 22px rgba(20,75,170,0.15)",
                transformOrigin: "right center",
                transform: showHelpTeaser ? "translateX(0px) scale(1)" : "translateX(18px) scale(0.96)",
                opacity: showHelpTeaser ? 1 : 0,
                maxWidth: showHelpTeaser ? 420 : 0,
                overflow: "hidden",
                transition:
                  "transform 340ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms ease, max-width 340ms ease",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: 0.1,
                  color: isDarkMode ? "#D8E7FF" : "#104C9A",
                }}
              >
                Learn how to make money with Incite?
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={() => {
              setHelpStepIndex(0);
              setIsHelpOpen(true);
              setHelpTeaserDismissed(true);
            }}
            aria-label="Open Incite help"
            sx={{
              pointerEvents: "auto",
              width: 38,
              height: 38,
              color: "#fff",
              background: "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
              border: "1px solid rgba(255,255,255,0.28)",
              boxShadow: "0 10px 26px rgba(25, 93, 210, 0.35)",
              animation: shouldPulseHelp ? `${helpPulse} 1.8s ease-out infinite` : "none",
              "&:hover": {
                background: "linear-gradient(135deg, #2f90ff 0%, #1c6af4 100%)",
              },
            }}
          >
            <HelpRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}

      {/* Floating launcher */}
      <Box
        sx={{
          position: "fixed",
          top: 8,
          right: 8,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          pointerEvents: "none",
        }}
      >
        <IconButton
          onClick={() => setIsDataProcessesOpen((o) => !o)}
          sx={{
            pointerEvents: "auto",
            position: "relative",
            "&:hover": {
              backgroundColor: isDarkMode ? "#2b2b2b" : "#D6D6D6",
            },
            ...(showProcessingLoader && {
              // subtle pulsing ring
              "&::after": {
                content: '""',
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                border: isDarkMode ? "3px solid rgba(255,255,255,0.55)" : "2px solid rgba(0,0,0,0.45)",
                animation: `${pulse} 1.5s ease-out infinite`,
                pointerEvents: "none",
              },
            }),
          }}
          aria-label={
            isDataProcessesOpen ? "Collapse data output" : "Expand data output"
          }
          size="small"
        >
          <Box
            component="img"
            src={isDarkMode ? MarketProLogo : MarketProLogoLight}
            sx={{
              width: "36px",
              animation: "none",
            }}
            className="logo-img"
            alt="IncitePlus Logo"
          />
        </IconButton>
      </Box>

      {showInciteHelp && (
        helpExperience === "video" ? (
          <InciteProcessVideoDialog
            open={isHelpOpen}
            isDesktop={isDesktop}
            onClose={() => setIsHelpOpen(false)}
          />
        ) : (
          <InciteProcessDialog
            open={isHelpOpen}
            isDesktop={isDesktop}
            stepIndex={helpStepIndex}
            setStepIndex={setHelpStepIndex}
            onClose={() => setIsHelpOpen(false)}
          />
        )
      )}

    {isDesktop ? (
      <Drawer
        anchor="right"
        variant="persistent"
        open={isDataProcessesOpen}
        sx={{
          "& .MuiDrawer-paper": {
            width: panelWidth,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#F9F9F9",
            borderLeft: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            boxShadow: isDarkMode
              ? "rgba(0,0,0,0.4) 0px 10px 30px"
              : "rgba(0,0,0,0.08) 0px 10px 30px",
            display: "flex",
            flexDirection: "column",
            transition: isResizingPanel ? "none" : "width 120ms ease",
          },
        }}
      >
        <Box
          aria-label="Resize data processes panel"
          aria-orientation="vertical"
          role="separator"
          tabIndex={0}
          title={`Drag to resize (${DATA_PROCESSES_MIN_WIDTH}-${DATA_PROCESSES_MAX_WIDTH}px)`}
          onKeyDown={handleResizeKeyDown}
          onPointerDown={handleResizePointerDown}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 10,
            zIndex: 4,
            cursor: "col-resize",
            touchAction: "none",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 2,
              backgroundColor: isResizingPanel
                ? "#2563EB"
                : (isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(71,85,105,0.18)"),
              transition: "background-color 140ms ease, width 140ms ease",
            },
            "&:hover::before": {
              width: 3,
              backgroundColor: "#2563EB",
            },
          }}
        />
        {/* Title Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 400, fontSize: "14px", letterSpacing: 0.5, color: isDarkMode ? "#f0f0f0" : "#111" }}
          >
            {hasAnalysisTrace ? "Analysis Trace" : "Data Processes"}
          </Typography>
        </Box>
        {processContent}
      </Drawer>
    ) : (
      <SwipeableDrawer
        anchor="right"
        open={isDataProcessesOpen}
        onOpen={() => setIsDataProcessesOpen(true)}
        onClose={() => setIsDataProcessesOpen(false)}
        swipeAreaWidth={24}
        keepMounted
        sx={{
          "& .MuiDrawer-paper": {
            width: `min(92vw, ${panelWidth}px)`,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#F9F9F9",
            borderLeft: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            boxShadow: isDarkMode
              ? "rgba(0,0,0,0.4) 0px 10px 30px"
              : "rgba(0,0,0,0.08) 0px 10px 30px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Title Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 400, fontSize: "14px", letterSpacing: 0.5, color: isDarkMode ? "#f0f0f0" : "#111" }}
          >
            {hasAnalysisTrace ? "Analysis Trace" : "Data Processes"}
          </Typography>
        </Box>
        {processContent}
      </SwipeableDrawer>
    )}
    </>
  );
};

export default DataOutput;
